import { setDomContext, setWindowContext } from '@/helpers/context';
import { debounce } from '@/helpers/utilities';
import { saveVehicleApi, saveSearchApi } from '@/helpers/saved/api';
import policyApi from '@/helpers/auth/api';
import { getCommonHeaders } from '@/helpers/saved/';

export default {
  async checkGetSavedVehicleIds({ dispatch, state }) {
    if (!state.user?.email) return;

    if (state.isSavedVehiclesLoading || state.isGotSavedVahicleIds) return;

    dispatch('getSavedVehicleIds');
  },

  async getSavedVehicleIds({ commit, rootState, state, dispatch }) {
    const {
      groupHash,
      franchiseHash,
      locationHash,
      groupSoftwareHash,
    } = rootState.ndc.app.context.site;

    try {
      commit('setValue', {
        key: 'isSavedVehiclesLoading',
        value: true,
      });

      await dispatch('getToken');
      const response = await saveVehicleApi.get('/vehicles', {
        headers: getCommonHeaders(
          { groupHash, franchiseHash, locationHash, groupSoftwareHash },
          state.token,
        ),
      });

      const vehicles = response.data;
      commit('setValues', [
        {
          key: 'savedVehicleIds',
          value: vehicles,
        },
        {
          key: 'isGotSavedVahicleIds',
          value: true,
        },
      ]);
    } catch (err) {
      console.log('error', err);
    } finally {
      commit('setValue', {
        key: 'isSavedVehiclesLoading',
        value: false,
      });
    }
  },

  async getPolicyContent({ commit, rootState }) {
    try {
      const baseUrl = rootState.ndc.app.context.site.ownershipUrl;
      const response = await policyApi(baseUrl).get('content.json');
      const policyContent = response.data;
      commit('setValue', {
        key: 'policyContent',
        value: policyContent,
      });
    } catch (err) {
      console.log('error', err.message);
      throw err;
    }
  },

  async saveVehicle({ state, commit, rootState, dispatch }, payload) {
    const {
      groupHash,
      franchiseHash,
      locationHash,
      groupSoftwareHash,
    } = rootState.ndc.app.context.site;

    try {
      const { vehicle, siteUrl } = payload;
      const data = {
        vehicle,
        siteUrl,
      };

      await dispatch('getToken');
      await saveVehicleApi.post('/vehicles', data, {
        headers: getCommonHeaders(
          { groupHash, franchiseHash, locationHash, groupSoftwareHash },
          state.token,
        ),
      });
      // append new id into save vehicle ids list
      const savedVehicleIds = [...state.savedVehicleIds, vehicle.id];
      commit('setValue', {
        key: 'savedVehicleIds',
        value: savedVehicleIds,
      });
    } catch (err) {
      console.log('error', err);
      throw err;
    }
  },

  async deleteVehicleById({ state, commit, rootState, dispatch }, payload) {
    const {
      groupHash,
      franchiseHash,
      locationHash,
      groupSoftwareHash,
    } = rootState.ndc.app.context.site;

    try {
      const id = payload;
      await dispatch('getToken');
      await saveVehicleApi.delete(`/vehicles/${id}`, {
        headers: getCommonHeaders(
          { groupHash, franchiseHash, locationHash, groupSoftwareHash },
          state.token,
        ),
      });

      // remove id in save vehicle ids list
      const savedVehicleIds = [...state.savedVehicleIds].filter((x) => x !== id);
      commit('setValue', {
        key: 'savedVehicleIds',
        value: savedVehicleIds,
      });
    } catch (err) {
      console.log('error', err);
      throw err;
    }
  },

  async getToken({ state, commit, dispatch }) {
    try {
      if (!state.initialized) {
        await dispatch('loadScript');
        return;
      }

      // get a token to be used on network requests
      const token = await window.ndAuth.getToken();
      commit('setValue', {
        key: 'token',
        value: token,
      });
    } catch (error) {
      console.log(error);
    }
  },

  async getUser({ state, commit, dispatch }) {
    try {
      if (!state.initialized) {
        await dispatch('loadScript');
        return;
      }

      // get the user details and store if they are available
      const user = await window.ndAuth.getUser();
      commit('setValue', {
        key: 'user',
        value: user,
      });
    } catch (error) {
      console.log(error);
    }
  },

  async ndAuthInit({ state, rootGetters, commit, dispatch }) {
    // initiate the auth library
    try {
      await window.ndAuthInit({
        clientId: state.context.auth.clientId,
        locale: rootGetters['ndc/app/locale'],
      });
      commit('setValue', {
        key: 'initialized',
        value: true,
      });
      dispatch('emitAuthInitializedEvent');
    } catch (error) {
      commit('setValues', [
        {
          key: 'error',
          value: error.message,
        },
        {
          key: 'initialized',
          value: false,
        },
      ]);
    }

    // do not continue if there is an error
    if (state.error) return;

    await Promise.all([dispatch('getToken'), dispatch('getUser')]);
    dispatch('emitAuthenticatedEvent');

    // watch for any authentication to update the user details
    window.ndAuth.on('authenticated', async () => {
      await Promise.all([dispatch('getToken'), dispatch('getUser')]);
      dispatch('emitAuthenticatedEvent');
    });

    // watch for any errors
    window.ndAuth.on('authorization_error', (error) => {
      commit('setValue', {
        key: 'error',
        value: error,
      });
    });
  },

  registerAsGuest({ commit }, { email }) {
    commit('setValue', {
      key: 'guest',
      value: { email },
    });
  },

  async checkToLoadScript({ dispatch, state }) {
    if (state.isInitializing || state.initialized) return;
    await dispatch('loadScript');
  },

  loadScript({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      commit('setValue', {
        key: 'isInitializing',
        value: true,
      });

      const headElement = document.getElementsByTagName('head')[0];
      const script = document.createElement('script');
      script.id = 'auth-library';
      script.async = 'true';
      script.src = process.env.VUE_APP_AUTH_LIBRARY;
      script.onload = async () => {
        await dispatch('ndAuthInit');
        resolve(true);
      };
      script.onerror = (e) => {
        reject(e);
      };
      headElement.appendChild(script);
    });
  },

  async logout({ rootGetters, dispatch }) {
    try {
      await dispatch('checkToLoadScript');

      const returnUrl = rootGetters['ndc/app/getListingUrl'];
      window.ndAuth.signOut({
        returnUrl,
      });
    } catch (error) {
      console.log(error);
    }
  },

  async saveSearch({ state, rootState, dispatch }, payload) {
    const {
      groupHash,
      franchiseHash,
      locationHash,
      groupSoftwareHash,
    } = rootState.ndc.app.context.site;

    try {
      await dispatch('getToken');
      await saveSearchApi.post('/searches', payload, {
        headers: getCommonHeaders(
          { groupHash, franchiseHash, locationHash, groupSoftwareHash },
          state.token,
        ),
      });
    } catch (err) {
      console.log('error', err);
      throw err;
    }
  },

  emitAuthenticatedEvent({ getters }) {
    const { isUserLogged } = getters;
    if (!isUserLogged) return;

    const message = { event: 'authenticated' };
    window.postMessage(JSON.stringify(message), '*');
  },

  emitAuthInitializedEvent() {
    const message = { event: 'initilized' };
    window.postMessage(JSON.stringify(message), '*');
  },

  setContext: debounce(({ commit, state }) => {
    const { context } = state;
    const domContext = setDomContext(context);
    const windowContext = setWindowContext(context);
    const value = { ...context, ...domContext, ...windowContext };

    commit('setValue', {
      key: 'context',
      value,
    });
  }, 100, { leading: true, trailing: false }),

  async afterModuleLoadedOnce({ dispatch }) {
    dispatch('setContext');
  },
};
